import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ChipSelect from "./shared/ChipSelect";
import FormCard from "./shared/FormCard";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import { executeIntent } from "../../experience/utils/IntentExecutor";
import IntentType from "../../intentexecutor/utils/IntentType";
import { Spin } from "antd";
import apiService from "../../service/apiservice/apiService";
import { workforceDataUpdateResponse } from "../../experience/carecoordinator/workforce/AddEditWorkforceModal";
import { EventType } from "../../service/network/utils/EventType";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color: "var(--main-theme-color)",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        mb: 1,
        mt: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

/** True when `options` has at least one non-empty list for the given field defs. */
function hasAnyFieldOptions(options, fieldDefs) {
  if (!options) return false;
  return fieldDefs.some((def) => Boolean(options[def.optionKey]));
}

function PreferenceChipFields({ options, formData, handleChange, fieldDefs }) {
  return fieldDefs.map((def) => {
    const chipOptions = options?.[def.optionKey];
    if (!chipOptions) return null;

    return (
      <FormCard
        key={def.optionKey}
        title={def.title}
        description={def.description}
      >
        <ChipSelect
          options={chipOptions}
          mode={def.mode}
          value={formData[def.optionKey]}
          onChange={(v) => handleChange(def.optionKey, v)}
        />
      </FormCard>
    );
  });
}

const PERSONALITY_FIELD_DEFS = [
  {
    optionKey: "patientPreference",
    title: "Patient types you're comfortable with",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "languages",
    title: "Languages spoken",
    description: "Select all that apply",
    mode: "multi",
  },
];

const SKILLS_FIELD_DEFS = [
  {
    optionKey: "skills",
    title: "Skills you can offer",
    description: "Select all that apply",
    mode: "multi",
  },
];

const SCHEDULING_AVAILABILITY = [
  {
    optionKey: "availability",
    title: "Availability",
    description: "Select all that apply",
    mode: "multi",
  },
];

const PETS_FIELD_DEFS = [
  {
    optionKey: "petComfort",
    title: "How do you feel about pets in the home?",
    mode: "single",
  },
  {
    optionKey: "petSensitivityOptions",
    title: "Any concerns or allergies to pets?",
    description: "Select all that apply",
    mode: "multi",
  },
];

const SMOKING_FIELD_DEFS = [
  {
    optionKey: "smokingTolerance",
    title: "Smoking Tolerance",
    mode: "single",
  },
];

const VETERAN_FIELD_DEFS = [
  {
    optionKey: "branchOfService",
    title: "Branch of Service",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "conflictServed",
    title: "Served in Conflict",
    description: "Select all that apply",
    mode: "multi",
  },
];

const PREFERENCE_SECTION_DIVIDER_SX = {
  borderColor: "#e5e7eb",
  my: 1.75,
};

const PREFERENCE_SUBSECTION_DIVIDER_SX = {
  borderColor: "#e5e7eb",
  my: 1.25,
};

// ─── Main Component ───────────────────────────────────────────────────────────

const VolunteerPrefernceForm = ({
  formId,
  userIdentifier,
  onLoadingChange,
  setOpenForm,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setLoadingState = useCallback(
    (value) => {
      onLoadingChange?.(value);
    },
    [onLoadingChange],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoadingState(true);
      try {
        await apiService.post(
          "niramayafoundation/niramayaworkforce/api/v1/workforce/preference",
          { organizationIdentifier, userIdentifier, preference: formData },
          "Preferences saved successfully",
          "Could not save preferences",
        );
      } finally {
        setLoadingState(false);
        setOpenForm(false);
        workforceDataUpdateResponse.next({
          type: EventType.WORKFORCE_DATA_UPDATED,
        });
      }
    },
    [
      formData,
      organizationIdentifier,
      setLoadingState,
      setOpenForm,
      userIdentifier,
    ],
  );

  useEffect(() => {
    const getPreferenceOptions = async () => {
      setOptionsLoading(true);
      const payload = {
        configurationKey: ["niramaya.volunteer.preference.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier,
        },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);

      setOptions(options);
      setOptionsLoading(false);
    };

    getPreferenceOptions();
  }, [organizationIdentifier]);

  useEffect(() => {
    if (!userIdentifier) return;

    const getPreference = async () => {
      setPreferencesLoading(true);
      try {
        const response = await apiService.get(
          "niramayafoundation/niramayaworkforce/api/v1/workforce/preference",
          { organizationIdentifier, userIdentifier },
        );

        if (response) {
          setFormData((prev) => ({ ...prev, ...response }));
        }
      } finally {
        setPreferencesLoading(false);
      }
    };

    getPreference();
  }, [organizationIdentifier, userIdentifier]);

  const showPersonality = hasAnyFieldOptions(options, PERSONALITY_FIELD_DEFS);
  const showAvailability = hasAnyFieldOptions(options, SCHEDULING_AVAILABILITY);
  const showSkills = hasAnyFieldOptions(options, SKILLS_FIELD_DEFS);
  const showPets = hasAnyFieldOptions(options, PETS_FIELD_DEFS);
  const showSmoking = hasAnyFieldOptions(options, SMOKING_FIELD_DEFS);
  const showVeteran = hasAnyFieldOptions(options, VETERAN_FIELD_DEFS);

  const hasLaterSection =
    showSkills || showPets || showSmoking || showVeteran;
  const hasAfterSkills = showPets || showSmoking || showVeteran;
  const hasAfterPets = showSmoking || showVeteran;

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={handleSubmit}
      noValidate
      sx={{ width: "100%", boxSizing: "border-box" }}
    >
      {optionsLoading || preferencesLoading ? (
        <Box sx={{ height: 150 }}>
          <Spin size="medium" className="spinnerCenter" />
        </Box>
      ) : (
        <>
          {showPersonality && (
            <>
              <SectionLabel>Personality &amp; Style</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={PERSONALITY_FIELD_DEFS}
              />
              {(showAvailability || hasLaterSection) && (
                <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
              )}
            </>
          )}

          {showAvailability && (
            <>
              <SectionLabel>Scheduling / Availability</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={SCHEDULING_AVAILABILITY}
              />
              {hasLaterSection && (
                <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
              )}
            </>
          )}

          {showSkills && (
            <>
              <SectionLabel>Skills &amp; Interests</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={SKILLS_FIELD_DEFS}
              />
              {hasAfterSkills && (
                <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
              )}
            </>
          )}

          {showPets && (
            <>
              <SectionLabel>Pets</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={PETS_FIELD_DEFS}
              />
              {hasAfterPets && (
                <Divider sx={PREFERENCE_SUBSECTION_DIVIDER_SX} />
              )}
            </>
          )}

          {showSmoking && (
            <>
              <SectionLabel>Smoking</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={SMOKING_FIELD_DEFS}
              />
            </>
          )}

          {showVeteran && (
            <>
              <SectionLabel>Veteran Status</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={VETERAN_FIELD_DEFS}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default VolunteerPrefernceForm;
